import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { useDateLocale } from '@/hooks/useDateLocale';
import {
  Loader2, Calendar, Clock, MapPin, Route, Gauge,
  Play, Pause, SkipForward, SkipBack,
  Navigation, Compass, Mountain, Satellite,
  ChevronsUpDown, Check,
} from 'lucide-react';
import { format, startOfDay, endOfDay, formatDuration } from 'date-fns';
import { DateRange } from 'react-day-picker';
import { DashboardLayout } from '@/components/dashboard/DashboardLayout';
import { useTraccarSession } from '@/hooks/useTraccarSession';
import { useResellerAccess } from '@/hooks/useResellerAccess';
import { RouteReplayMap } from '@/components/history/RouteReplayMap';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';

import {
  fetchDevices,
  fetchTrips,
  fetchStops,
  fetchRoute,
  type Device,
  type Trip,
  type Stop,
  type Position,
} from '@/lib/traccar-api';
import { fetchDeviceMerges } from '@/lib/reseller-api';
import { cn } from '@/lib/utils';

const Trips = () => {
  const { t } = useTranslation();
  const dateFnsLocale = useDateLocale();
  const { isAuthenticated, user } = useTraccarSession();
  const { isReseller } = useResellerAccess();
  const shouldApplyMerges = !isReseller && !user?.administrator;
  const [searchParams] = useSearchParams();

  const [devices, setDevices] = useState<Device[]>([]);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [stops, setStops] = useState<Stop[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTrips, setIsLoadingTrips] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(() => {
    const param = searchParams.get('deviceId');
    return param ? Number(param) : null;
  });
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [selectedStop, setSelectedStop] = useState<Stop | null>(null);
  const [fullRoute, setFullRoute] = useState(true);
  const [vehicleOpen, setVehicleOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const dateParam = searchParams.get('date');
    const base = dateParam ? new Date(dateParam) : new Date();
    return { from: startOfDay(base), to: endOfDay(base) };
  });

  // Boundary positions for engine hours
  const [boundaryPositions, setBoundaryPositions] = useState<{ first: Position | null; last: Position | null }>({ first: null, last: null });

  // Route positions & playback
  const [routePositions, setRoutePositions] = useState<Position[]>([]);
  const [isLoadingRoute, setIsLoadingRoute] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const playIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTripResults = useCallback(() => {
    setTrips([]);
    setStops([]);
    setSelectedTrip(null);
    setSelectedStop(null);
    setBoundaryPositions({ first: null, last: null });
    setRoutePositions([]);
    setCurrentIndex(0);
    setPlaying(false);
    setIsLoadingTrips(false);
    setIsLoadingRoute(false);
  }, []);

  const { data: deviceMerges = [] } = useQuery({
    queryKey: ['device-merges'],
    queryFn: fetchDeviceMerges,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
  const mergeSecondaryIds = useMemo(() => {
    const set = new Set<number>();
    if (shouldApplyMerges) deviceMerges.forEach(m => set.add(m.secondary_device_id));
    return set;
  }, [deviceMerges, shouldApplyMerges]);
  const mergePrimaryOf = useMemo(() => {
    const map = new Map<number, number>();
    if (shouldApplyMerges) deviceMerges.forEach(m => map.set(m.secondary_device_id, m.primary_device_id));
    return map;
  }, [deviceMerges, shouldApplyMerges]);
  const mergeSecondaryOf = useMemo(() => {
    const map = new Map<number, number[]>();
    if (shouldApplyMerges) deviceMerges.forEach(m => {
      if (!map.has(m.primary_device_id)) map.set(m.primary_device_id, []);
      map.get(m.primary_device_id)!.push(m.secondary_device_id);
    });
    return map;
  }, [deviceMerges, shouldApplyMerges]);
  const visibleDevices = useMemo(() =>
    shouldApplyMerges ? devices.filter(d => !mergeSecondaryIds.has(d.id)) : devices,
    [devices, shouldApplyMerges, mergeSecondaryIds]
  );

  // If the auto-selected device is a secondary, redirect to its primary.
  useEffect(() => {
    if (!mergeSecondaryIds.size || !selectedDeviceId) return;
    if (mergeSecondaryIds.has(selectedDeviceId)) {
      setSelectedDeviceId(mergePrimaryOf.get(selectedDeviceId) ?? null);
    }
  }, [mergeSecondaryIds, mergePrimaryOf, selectedDeviceId]);

  // Sync deviceId from URL search params (e.g. navigating from map popup)
  useEffect(() => {
    const param = searchParams.get('deviceId');
    if (param) {
      setSelectedDeviceId(Number(param));
    }
  }, [searchParams]);

  const loadInitialData = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setIsLoading(true);
      const devicesData = await fetchDevices();
      if (devicesData.length > 0) {
        setDevices(devicesData);
        if (!selectedDeviceId && devicesData.length > 0) {
          setSelectedDeviceId(devicesData[0].id);
        }
      }
      setHasLoaded(true);
    } catch (error) {
      console.error('Failed to load initial data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, selectedDeviceId]);

  const loadTrips = useCallback(async (signal?: AbortSignal) => {
    if (!selectedDeviceId || !dateRange?.from || !dateRange?.to) {
      clearTripResults();
      return;
    }

    try {
      setIsLoadingTrips(true);
      const from = startOfDay(dateRange.from).toISOString();
      const to = endOfDay(dateRange.to).toISOString();

      const deviceIds = [
        selectedDeviceId,
        ...(shouldApplyMerges ? (mergeSecondaryOf.get(selectedDeviceId) ?? []) : []),
      ];
      const [tripsArrays, stopsArrays] = await Promise.all([
        Promise.all(deviceIds.map(id => fetchTrips(id, from, to, { signal }).catch((error) => {
          if (signal?.aborted) throw error;
          return [] as Trip[];
        }))),
        Promise.all(deviceIds.map(id => fetchStops(id, from, to, { signal }).catch((error) => {
          if (signal?.aborted) throw error;
          return [] as Stop[];
        }))),
      ]);
      if (signal?.aborted) return;
      setTrips(tripsArrays.flat().sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()));
      setStops(stopsArrays.flat().sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()));
      setSelectedTrip(null);
      setSelectedStop(null);
    } catch (error) {
      if (signal?.aborted) return;
      console.error('Failed to load trips:', error);
      setTrips([]);
      setStops([]);
      setSelectedTrip(null);
      setSelectedStop(null);
    } finally {
      if (!signal?.aborted) setIsLoadingTrips(false);
    }
  }, [selectedDeviceId, dateRange, shouldApplyMerges, mergeSecondaryOf, clearTripResults]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (!selectedDeviceId || !dateRange?.from || !dateRange?.to) {
      clearTripResults();
      return;
    }

    const controller = new AbortController();
    loadTrips(controller.signal);
    return () => controller.abort();
  }, [selectedDeviceId, dateRange, loadTrips, clearTripResults]);

  // Auto-select trip or stop matching ?time= param
  const timeParam = searchParams.get('time');
  const autoSelectedRef = useRef(false);
  useEffect(() => {
    if (!timeParam || autoSelectedRef.current) return;
    const t = parseInt(timeParam);
    if (isNaN(t)) return;

    if (trips.length > 0 || stops.length > 0) {
      const matchTrip = trips.find(trip =>
        new Date(trip.startTime).getTime() <= t && t <= new Date(trip.endTime).getTime()
      );
      if (matchTrip) {
        autoSelectedRef.current = true;
        setSelectedTrip(matchTrip);
        setSelectedStop(null);
        setFullRoute(false);
        return;
      }
      const matchStop = stops.find(stop =>
        new Date(stop.startTime).getTime() <= t && t <= new Date(stop.endTime).getTime()
      );
      if (matchStop) {
        autoSelectedRef.current = true;
        setSelectedStop(matchStop);
        setSelectedTrip(null);
        setFullRoute(false);
      }
    }
  }, [trips, stops, timeParam]);

  // Engine-hour counters come from the first and last positions that report hours.
  useEffect(() => {
    if (!selectedDeviceId || !dateRange?.from || !dateRange?.to) {
      setBoundaryPositions({ first: null, last: null });
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    const load = async () => {
      try {
        const from = startOfDay(dateRange.from!).toISOString();
        const to = endOfDay(dateRange.to!).toISOString();
        const positions = await fetchRoute(selectedDeviceId, from, to, { signal: controller.signal });
        if (!cancelled && positions.length > 0) {
          const withHours = positions.filter(p => p.attributes?.hours);
          setBoundaryPositions({ first: withHours[0] ?? null, last: withHours[withHours.length - 1] ?? null });
        } else if (!cancelled) {
          setBoundaryPositions({ first: null, last: null });
        }
      } catch {
        if (!cancelled) setBoundaryPositions({ first: null, last: null });
      }
    };
    load();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [selectedDeviceId, dateRange]);

  // Fetch route positions when a trip, stop, or full route is selected
  const selectedTimeRange = useMemo(() => {
    if (fullRoute && selectedDeviceId && dateRange?.from && dateRange?.to) {
      return {
        deviceId: selectedDeviceId,
        from: startOfDay(dateRange.from).toISOString(),
        to: endOfDay(dateRange.to).toISOString(),
      };
    }
    if (selectedTrip) {
      return { deviceId: selectedTrip.deviceId, from: selectedTrip.startTime, to: selectedTrip.endTime };
    }
    if (selectedStop) {
      return { deviceId: selectedStop.deviceId, from: selectedStop.startTime, to: selectedStop.endTime };
    }
    return null;
  }, [fullRoute, selectedDeviceId, dateRange, selectedTrip, selectedStop]);

  useEffect(() => {
    if (!selectedTimeRange) {
      setRoutePositions([]);
      setIsLoadingRoute(false);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    const load = async () => {
      setIsLoadingRoute(true);
      try {
        const route = await fetchRoute(selectedTimeRange.deviceId, selectedTimeRange.from, selectedTimeRange.to, { signal: controller.signal });
        if (!cancelled) setRoutePositions(route);
      } catch (error) {
        if (!controller.signal.aborted) console.error('Failed to fetch route:', error);
        if (!cancelled) setRoutePositions([]);
      } finally {
        if (!cancelled) setIsLoadingRoute(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [selectedTimeRange]);

  // Reset playback when selection changes
  useEffect(() => {
    setCurrentIndex(0);
    setPlaying(false);
  }, [selectedTrip, selectedStop, fullRoute]);

  // Seek to fixTime from URL param once route positions are loaded
  useEffect(() => {
    if (!timeParam || routePositions.length === 0) return;
    const target = new Date(parseInt(timeParam)).getTime();
    console.log('[seek] target:', new Date(target).toString())
    let closest = 0;
    let minDiff = Infinity;
    routePositions.forEach((pos, i) => {
      const diff = Math.abs(new Date(pos.fixTime).getTime() - target);
      if (diff < minDiff) { minDiff = diff; closest = i; }
    });
    setCurrentIndex(closest);
  }, [routePositions, timeParam]);

  // Playback interval
  useEffect(() => {
    if (playing && routePositions.length > 0) {
      playIntervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => {
          if (prev >= routePositions.length - 1) {
            setPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, 500 / playbackSpeed);
    }
    return () => {
      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current);
        playIntervalRef.current = null;
      }
    };
  }, [playing, playbackSpeed, routePositions.length]);

  const handlePlayPause = () => {
    if (routePositions.length === 0) return;
    if (currentIndex >= routePositions.length - 1) {
      setCurrentIndex(0);
      setPlaying(true);
    } else {
      setPlaying(!playing);
    }
  };

  const handleStepForward = () => {
    if (currentIndex < routePositions.length - 1) {
      setPlaying(false);
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleStepBackward = () => {
    if (currentIndex > 0) {
      setPlaying(false);
      setCurrentIndex(currentIndex - 1);
    }
  };

  const currentReplayPos = routePositions[currentIndex] || null;
  const hasActiveRoute = (selectedTrip || selectedStop || fullRoute) && routePositions.length > 0;

  const formatTripDuration = (ms: number) => {
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    return formatDuration({ hours, minutes }, { format: ['hours', 'minutes'], zero: false, locale: dateFnsLocale }) || '< 1 min';
  };

  const handleDateRangeSelect = useCallback((range: DateRange | undefined) => {
    setFullRoute(true);
    setSelectedTrip(null);
    setSelectedStop(null);

    if (!range) {
      setDateRange(undefined);
      clearTripResults();
      return;
    }

    setDateRange({
      from: range.from ? startOfDay(range.from) : undefined,
      to: range.to ? endOfDay(range.to) : (range.from ? endOfDay(range.from) : undefined),
    });
  }, [clearTripResults]);

  const totals = useMemo(() => {
    const base = trips.reduce(
      (acc, trip) => ({
        distance: acc.distance + (trip.distance || 0),
        duration: acc.duration + (trip.duration || 0),
        tripCount: acc.tripCount + 1,
        maxSpeed: Math.max(acc.maxSpeed, trip.maxSpeed || 0),
        weightedSpeed: acc.weightedSpeed + ((trip.averageSpeed || 0) * (trip.duration || 0)),
      }),
      { distance: 0, duration: 0, tripCount: 0, maxSpeed: 0, weightedSpeed: 0 }
    );
    const sorted = [...trips].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    return {
      ...base,
      averageSpeed: base.duration > 0 ? base.weightedSpeed / base.duration : 0,
      startOdometer: first?.startOdometer ?? 0,
      endOdometer: last?.endOdometer ?? 0,
      startHours: (boundaryPositions.first?.attributes?.hours as number | undefined) ?? 0,
      endHours: (boundaryPositions.last?.attributes?.hours as number | undefined) ?? 0,
    };
  }, [trips, boundaryPositions]);

  // Merge trips and stops into a chronological timeline
  type TimelineItem = { type: 'trip'; data: Trip } | { type: 'stop'; data: Stop };
  const timeline = useMemo<TimelineItem[]>(() => {
    const items: TimelineItem[] = [
      ...trips.map(t => ({ type: 'trip' as const, data: t })),
      ...stops.map(s => ({ type: 'stop' as const, data: s })),
    ];
    items.sort((a, b) => new Date(a.data.startTime).getTime() - new Date(b.data.startTime).getTime());
    return items;
  }, [trips, stops]);

  if (isLoading && !hasLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }


  const headerContent = (
    <div className="flex items-center gap-4">
      <Popover open={vehicleOpen} onOpenChange={setVehicleOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={vehicleOpen}
            className="w-[200px] justify-between"
          >
            <span className="truncate">
              {selectedDeviceId
                ? (visibleDevices.find(d => d.id === selectedDeviceId)?.name ?? t('history.selectDevice'))
                : t('history.selectDevice')}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0">
          <Command>
            <CommandInput placeholder={t('common.search') + '...'} />
            <CommandList>
              <CommandEmpty>{t('common.noResults')}</CommandEmpty>
              <CommandGroup>
                {visibleDevices.map(device => (
                  <CommandItem
                    key={device.id}
                    value={device.name}
                    onSelect={() => {
                      setSelectedDeviceId(device.id);
                      setVehicleOpen(false);
                    }}
                  >
                    <Check className={cn("mr-2 h-4 w-4 shrink-0", selectedDeviceId === device.id ? "opacity-100" : "opacity-0")} />
                    {device.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="gap-2">
            <Calendar className="h-4 w-4" />
            {dateRange?.from ? (
              dateRange.to ? (
                <>
                  {format(dateRange.from, "MMM d", { locale: dateFnsLocale })} - {format(dateRange.to, "MMM d, yyyy", { locale: dateFnsLocale })}
                </>
              ) : (
                format(dateRange.from, "MMM d, yyyy", { locale: dateFnsLocale })
              )
            ) : (
              t('history.selectDates')
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <CalendarComponent
            mode="range"
            selected={dateRange}
            onSelect={handleDateRangeSelect}
            numberOfMonths={2}
            disabled={(date) => date > new Date()}
          />
        </PopoverContent>
      </Popover>
    </div>
  );

  return (
    <DashboardLayout headerContent={headerContent}>
      <div className="absolute inset-0 flex flex-col overflow-hidden">
        {/* Playback bar */}
        {hasActiveRoute && (
          <div className="shrink-0 z-30 bg-background/80 backdrop-blur-md border-b px-6 py-2">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  onClick={handleStepBackward}
                  disabled={currentIndex === 0}
                >
                  <SkipBack className="h-3 w-3" />
                </Button>
                <Button
                  variant="default"
                  size="icon"
                  onClick={handlePlayPause}
                  className="h-8 w-8"
                >
                  {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  onClick={handleStepForward}
                  disabled={currentIndex >= routePositions.length - 1}
                >
                  <SkipForward className="h-3 w-3" />
                </Button>
              </div>
              <Slider
                value={[currentIndex]}
                min={0}
                max={Math.max(0, routePositions.length - 1)}
                step={1}
                onValueChange={([val]) => {
                  setPlaying(false);
                  setCurrentIndex(val);
                }}
                className="flex-1"
              />
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-muted-foreground tabular-nums w-20 text-center">
                  {currentReplayPos
                    ? format(new Date(currentReplayPos.fixTime), 'HH:mm:ss')
                    : `${currentIndex + 1}/${routePositions.length}`}
                </span>
                <div className="flex items-center gap-1">
                  {[1, 2, 5, 10].map((speed) => (
                    <Button
                      key={speed}
                      variant={playbackSpeed === speed ? "default" : "outline"}
                      size="sm"
                      className="h-6 px-1.5 text-[10px]"
                      onClick={() => setPlaybackSpeed(speed)}
                    >
                      {speed}x
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Current position data */}
        {hasActiveRoute && currentReplayPos && (
          <div className="shrink-0 z-20 bg-muted/50 border-b px-6 py-1.5">
            <div className="flex items-center gap-4 text-xs flex-nowrap overflow-x-auto">
              <span className="flex items-center gap-1 font-medium shrink-0">
                <Clock className="h-3 w-3 text-muted-foreground" />
                {format(new Date(currentReplayPos.fixTime), 'MMM d, HH:mm:ss', { locale: dateFnsLocale })}
              </span>
              {currentReplayPos.serverTime && (() => {
                const diff = Math.round((new Date(currentReplayPos.serverTime).getTime() - new Date(currentReplayPos.fixTime).getTime()) / 1000);
                return diff > 0 ? (
                  <span className="text-muted-foreground shrink-0">
                    +{diff >= 60 ? `${Math.floor(diff / 60)}m ${diff % 60}s` : `${diff}s`}
                  </span>
                ) : null;
              })()}
              <span className="flex items-center gap-1 shrink-0">
                <Gauge className="h-3 w-3 text-blue-500" />
                {Math.round(currentReplayPos.speed * 1.852)} km/h
              </span>
              {currentReplayPos.attributes?.ignition !== undefined && (
                <span className={cn('flex items-center gap-0.5 shrink-0', currentReplayPos.attributes.ignition ? 'text-green-600' : 'text-muted-foreground')}>
                  <i className={currentReplayPos.attributes.ignition ? 'i-ignition-on' : 'i-ignition-off'} style={{ fontSize: '14px' }} />
                  <span>{currentReplayPos.attributes.ignition ? t('performance.on', 'ON') : t('performance.off', 'OFF')}</span>
                </span>
              )}
              <span className="flex items-center gap-1 shrink-0">
                <Mountain className="h-3 w-3 text-green-600" />
                {Math.round(currentReplayPos.altitude)} m
              </span>
              <span className="flex items-center gap-1 shrink-0">
                <Compass className="h-3 w-3 text-orange-500" />
                {Math.round(currentReplayPos.course)}°
              </span>
              <span className="flex items-center gap-1 shrink-0">
                <Navigation className="h-3 w-3 text-muted-foreground" />
                {currentReplayPos.latitude.toFixed(5)}, {currentReplayPos.longitude.toFixed(5)}
              </span>
              {currentReplayPos.attributes?.sat !== undefined && (
                <span className="flex items-center gap-1 shrink-0">
                  <Satellite className="h-3 w-3 text-muted-foreground" />
                  {String(currentReplayPos.attributes.sat)}
                </span>
              )}
              {currentReplayPos.address && (
                <span className="text-muted-foreground shrink-0 truncate max-w-[300px]" title={currentReplayPos.address}>
                  {currentReplayPos.address}
                </span>
              )}
            </div>
          </div>
        )}

        <div className="p-6 flex-1 min-h-0 overflow-hidden grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
          <div className="min-h-0 flex flex-col gap-4">
          {/* Summary Cards */}
          <div className="shrink-0 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{t('history.totalTrips')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Route className="h-5 w-5 text-primary" />
                  <span className="text-lg font-bold">{totals.tripCount}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{t('history.totalDistance')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  <div className="flex flex-col">
                    <span className="text-lg font-bold">{(totals.distance / 1000).toFixed(1)} km</span>
                    {(totals.startOdometer > 0 || totals.endOdometer > 0) && (
                      <span className="text-xs text-muted-foreground">
                        {(totals.startOdometer / 1000).toFixed(0)} → {(totals.endOdometer / 1000).toFixed(0)} km
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{t('history.totalDuration')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  <div className="flex flex-col">
                    <span className="text-lg font-bold">{totals.duration > 0 ? formatTripDuration(totals.duration) : '-'}</span>
                    {(totals.startHours > 0 || totals.endHours > 0) && (
                      <span className="text-xs text-muted-foreground">
                        {(totals.startHours / 3600000).toFixed(1)} → {(totals.endHours / 3600000).toFixed(1)} h
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{t('history.maxSpeed')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Gauge className="h-5 w-5 text-orange-500" />
                  <div className="flex flex-col">
                    <span className="text-lg font-bold">
                      {totals.maxSpeed > 0 ? `${Math.round(totals.maxSpeed * 1.852)} km/h` : '-'}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {t('history.avgSpeed')}: {totals.averageSpeed > 0 ? `${Math.round(totals.averageSpeed * 1.852)} km/h` : '-'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Route Map */}
          <div className="flex-1 min-h-0">
            {/* Route Map */}
            <div className="h-full min-h-0 relative rounded-lg border bg-card overflow-hidden">
              <div className="absolute inset-0">
                <RouteReplayMap
                  positions={routePositions}
                  currentIndex={currentIndex}
                  onIndexChange={(idx) => {
                    setPlaying(false);
                    setCurrentIndex(idx);
                  }}
                  focusTime={timeParam ? parseInt(timeParam) : null}
                  className="h-full"
                  playing={playing}
                  onPlayPause={handlePlayPause}
                  onStepForward={handleStepForward}
                  onStepBackward={handleStepBackward}
                  playbackSpeed={playbackSpeed}
                  onSpeedChange={setPlaybackSpeed}
                />
                {isLoadingRoute && (
                  <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-20">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                )}
              </div>
            </div>
          </div>
          </div>

            {/* Trips & Stops Timeline */}
            <Card className="hidden lg:flex min-h-0 flex-col overflow-hidden">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Route className="h-5 w-5" />
                  {t('history.title')}
                  <Badge variant="secondary" className="ml-1 text-xs">{trips.length}</Badge>
                  <Button
                    variant={fullRoute ? "default" : "outline"}
                    size="sm"
                    className="ml-auto h-7 px-2 text-xs"
                    onClick={() => {
                      setFullRoute(!fullRoute);
                      if (!fullRoute) {
                        setSelectedTrip(null);
                        setSelectedStop(null);
                      }
                    }}
                    disabled={!selectedDeviceId || !dateRange?.from || !dateRange?.to}
                  >
                    {t('performance.fullRoute')}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 min-h-0 overflow-y-auto">
                {isLoadingTrips ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : timeline.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Route className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t('history.noTrips')}</p>
                    <p className="text-sm mt-1">{t('history.noTripsHint')}</p>
                  </div>
                ) : (
                  <div className="space-y-2 pr-2">
                    {timeline.map((item, index) => {
                      if (item.type === 'trip') {
                        const trip = item.data;
                        const isSelected = selectedTrip === trip;
                        return (
                          <div
                            key={`trip-${trip.startTime}-${index}`}
                            ref={isSelected && timeParam ? (el) => el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }) : null}
                            className={cn(
                              "border rounded-lg p-3 cursor-pointer transition-colors",
                              isSelected
                                ? "border-primary bg-primary/5"
                                : "hover:bg-muted/50"
                            )}
                            onClick={() => { setSelectedTrip(trip); setSelectedStop(null); setFullRoute(false); }}
                          >
                            <div className="flex items-center gap-2 text-sm">
                              <Route className="h-3 w-3 text-blue-500 shrink-0" />
                              {format(new Date(trip.startTime), 'MMM d, HH:mm', { locale: dateFnsLocale })} — {format(new Date(trip.endTime), 'HH:mm')}
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {(trip.distance / 1000).toFixed(1)} km
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatTripDuration(trip.duration)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Gauge className="h-3 w-3" />
                                {Math.round(trip.maxSpeed * 1.852)} km/h
                              </span>
                            </div>
                            {isSelected && (
                              <div className="mt-3 pt-3 border-t space-y-2 text-sm">
                                <div className="flex items-start gap-2">
                                  <div className="h-2 w-2 rounded-full bg-green-500 mt-1.5 shrink-0" />
                                  <span className="text-muted-foreground">{trip.startAddress || t('map.addressUnavailable')}</span>
                                </div>
                                <div className="flex items-start gap-2">
                                  <div className="h-2 w-2 rounded-full bg-red-500 mt-1.5 shrink-0" />
                                  <span className="text-muted-foreground">{trip.endAddress || t('map.addressUnavailable')}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      } else {
                        const stop = item.data;
                        const isStopSelected = selectedStop === stop;
                        return (
                          <div
                            key={`stop-${stop.startTime}-${index}`}
                            className={cn(
                              "border border-dashed rounded-lg p-3 cursor-pointer transition-colors",
                              isStopSelected ? "border-red-400 bg-red-500/5" : "opacity-75 hover:bg-muted/50"
                            )}
                            onClick={() => { setSelectedStop(stop); setSelectedTrip(null); setFullRoute(false); }}
                          >
                            <div className="flex items-center gap-2 text-sm">
                              <MapPin className="h-3 w-3 text-red-400 shrink-0" />
                              {format(new Date(stop.startTime), 'MMM d, HH:mm', { locale: dateFnsLocale })} — {format(new Date(stop.endTime), 'HH:mm')}
                            </div>
                            {stop.address && (
                              <p className="text-[11px] text-muted-foreground mt-0.5 truncate" title={stop.address}>
                                {stop.address}
                              </p>
                            )}
                            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                              <span>{formatTripDuration(stop.duration)}</span>
                              {stop.engineHours > 0 && <span>⏱ {formatTripDuration(stop.engineHours)}</span>}
                            </div>
                          </div>
                        );
                      }
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
    </DashboardLayout>
  );
};

export default Trips;
